import { appConfig } from "./config/env.js";
import { copy } from "./config/copy.js";
import { Store } from "./core/store.js";
import { Router } from "./core/router.js";
import { createBackStack } from "./core/backStack.js";
import { createToggle } from "./core/preference.js";
import { h } from "./core/dom.js";
import { ModelLoader, ModelNotFoundError } from "./inference/modelLoader.js";
import { DogAffectClassifier } from "./inference/classifier.js";
import { DogDetector } from "./inference/detector.js";
import { HybridAnalyzer } from "./inference/hybridAnalyzer.js";
import { RemoteClassifier } from "./inference/remoteClassifier.js";
import { ExplainClient } from "./llm/explainClient.js";
import { SpeechClient } from "./llm/speechClient.js";
import { CoachClient } from "./llm/coachClient.js";
import { FeedbackClient } from "./feedback/feedbackClient.js";
import { HistoryStore } from "./storage/historyStore.js";
import { ErrorReporter } from "./telemetry/errorReporter.js";
import { DevAccess } from "./dev/devAccess.js";
import { createDevMode } from "./dev/devMode.js";
import { pendingEntry } from "./dev/changelog.js";
import { trackAppVersion, trackModelVersion } from "./dev/localBuild.js";
import { createToaster } from "./ui/components/toast.js";
import { createTheme } from "./ui/theme.js";
import { createSpeaker } from "./ui/speech.js";
import { createCoach } from "./ui/coach.js";
import { changelogSheet } from "./ui/components/changelogSheet.js";
import { brandBadge } from "./ui/components/brand.js";
import { modelStatusChip } from "./ui/components/modelStatus.js";
import { ResultSheet } from "./ui/components/resultSheet.js";
import { analyzeView } from "./ui/views/analyzeView.js";
import { historyView } from "./ui/views/historyView.js";
import { infoView } from "./ui/views/infoView.js";
import { devView } from "./ui/views/devView.js";

export function createApp(root) {
  const store = new Store({ modelStatus: "idle", modelProgress: 0 });
  const reporter = new ErrorReporter(appConfig.apiBase, appConfig.version);
  const classifier = new DogAffectClassifier({
    loader: new ModelLoader(appConfig.modelBase, { variant: appConfig.modelVariant }),
    ortLoader: () => import(/* @vite-ignore */ `${appConfig.ortWasmBase}ort.wasm.min.mjs`),
    wasmBase: appConfig.ortWasmBase
  });
  const detector = new DogDetector({
    loader: new ModelLoader(appConfig.modelBase, { variant: "fp32", cachePrefix: "paws-detector-" }),
    ortProvider: () => classifier.ort
  });
  const remote = new RemoteClassifier(appConfig.serverUrl);
  const outlet = h("main", { class: "outlet" });
  const app = {
    config: appConfig,
    store,
    reporter,
    classifier,
    detector,
    remote,
    boxes: createToggle("paws.boxes", true),
    analyzer: new HybridAnalyzer(classifier, remote, reporter),
    explain: new ExplainClient(appConfig.apiBase),
    speech: new SpeechClient(appConfig.apiBase),
    coachClient: new CoachClient(appConfig.apiBase),
    coachEnabled: createToggle("paws.coach", true),
    coachLive: createToggle("paws.coach.live", false),
    devMode: createDevMode(),
    updates: { app: null, model: null },
    feedback: new FeedbackClient(appConfig.apiBase, appConfig.version),
    history: new HistoryStore(),
    dev: new DevAccess(appConfig),
    toast: createToaster(),
    theme: createTheme(),
    back: createBackStack(),
    speaker: null,
    router: new Router(outlet)
  };
  app.speaker = createSpeaker({ client: app.speech, onError: (error) => reporter.report(error, "voice") });
  app.coach = createCoach({
    client: app.coachClient,
    speaker: app.speaker,
    toggle: app.coachEnabled,
    onError: (error) => reporter.report(error, "coach")
  });
  app.updates.app = trackAppVersion(appConfig.version);
  app.updates.model = trackModelVersion(null);
  app.sheet = new ResultSheet(app);
  window.addEventListener("hashchange", () => app.sheet.close());

  const header = h("header", { class: "topbar" }, brandBadge().wrapper, modelStatusChip(store));
  const nav = h("nav", { class: "tabbar" },
    h("a", { href: "#/", "data-nav": true }, h("span", {}, "📷"), "Analizza"),
    h("a", { href: "#/history", "data-nav": true }, h("span", {}, "🕘"), "Storico"),
    h("a", { href: "#/info", "data-nav": true }, h("span", {}, "ℹ️"), "Info"));
  root.replaceChildren(header, outlet, nav);

  app.router
    .register("/", () => analyzeView(app))
    .register("/history", () => historyView(app))
    .register("/info", () => infoView(app))
    .register("/dev", () => (app.devMode.enabled ? devView(app) : infoView(app)));

  return {
    app,
    async start() {
      reporter.install();
      remote.warmUp();
      window.addEventListener("offline", () => app.toast(copy.offline, "warning", 5000));
      app.router.resolve();
      showChangelog(app);
      await loadModel(app);
    }
  };
}

export function showChangelog(app) {
  if (!app.devMode.enabled) return null;
  const entry = pendingEntry({ appVersion: app.config.version });
  if (!entry) return null;
  return changelogSheet(app, entry);
}

export async function loadModel(app) {
  app.store.set({ modelStatus: "loading" });
  try {
    await app.classifier.init((pct) => app.store.set({ modelStatus: pct < 100 ? "downloading" : "loading", modelProgress: pct }));
    app.store.set({ modelStatus: "ready" });
    app.updates.model = trackModelVersion(app.classifier.metadata?.model_version);
    app.detector.init(app.classifier.metadata).catch((error) => app.reporter.report(error, "detector-init"));
  } catch (error) {
    const online = Boolean(app.config.serverUrl);
    if (error instanceof ModelNotFoundError) {
      app.store.set({ modelStatus: online ? "online" : "missing" });
    } else {
      app.store.set({ modelStatus: online ? "online" : "error" });
      app.reporter.report(error, "model-load");
      if (!online) app.toast(copy.modelError, "error", 6000);
    }
  }
}
